import React, { createContext, useContext, useState } from "react";
import * as partnerService from "../services/partnerService";
import { useNotification } from "./NotificationContext";
import axios from "axios";

// Types
export interface Partner {
  _id: string;
  name: string;
  image: string;
  createdAt: string;
  updatedAt: string;
}

interface PartnerContextType {
  partners: Partner[];
  loading: boolean;
  error: string | null;
  fetchPartners: () => Promise<void>;
  createPartner: (name: string, imageFile: File) => Promise<void>;
  deletePartner: (id: string) => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const handleApiError = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      setError(errorMessage);
      showError(errorMessage);
    } else {
      const errorMessage = "An unexpected error occurred";
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnerService.getPartners();
      setPartners(data);
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const createPartner = async (name: string, imageFile: File) => {
    setLoading(true);
    setError(null);
    try {
      const partner = await partnerService.createPartner(name, imageFile);
      setPartners((prev) => [...prev, partner]);
      showSuccess("Partner created successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const deletePartner = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await partnerService.deletePartner(id);
      setPartners((prev) => prev.filter((p) => p._id !== id));
      showSuccess("Partner deleted successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PartnerContext.Provider
      value={{
        partners,
        loading,
        error,
        fetchPartners,
        createPartner,
        deletePartner,
      }}
    >
      {children}
    </PartnerContext.Provider>
  );
};

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (!context)
    throw new Error("usePartner must be used within a PartnerProvider");
  return context;
};
